/** Support → refund requests: opened by the support, decided by the accountant or the manager. */

import { useSupportRefundsDecideCreate, useSupportRefundsList } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton, TextField } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { fieldErrors } from "../../lib/settings";
import { dateTimeText } from "../billing/shared";
import { useRefreshSupport, useSupportPermissions } from "./shared";

export function RefundsTab() {
  const { t, i18n } = useTranslation();
  const can = useSupportPermissions();
  const refresh = useRefreshSupport();
  const list = useSupportRefundsList({});
  const decide = useSupportRefundsDecideCreate({ mutation: { onSuccess: refresh } });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  const errors = fieldErrors(decide.error);
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-refunds-title">
      <h2 id="support-refunds-title">{t("support.tabs.refunds")}</h2>
      <p className="muted">{t("support.refunds.intro")}</p>
      {list.isPending ? <Skeleton /> : null}
      {!list.isPending && !rows.length ? <p className="muted">{t("support.refunds.empty")}</p> : null}
      {rows.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t("support.refunds.ticket")}</th>
                <th scope="col">{t("support.refunds.amount")}</th>
                <th scope="col">{t("support.refunds.reason")}</th>
                <th scope="col">{t("support.refunds.requestedBy")}</th>
                <th scope="col">{t("support.refunds.status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/support/tickets/${row.ticket}`}>{row.ticket_number}</Link>
                    <br />
                    <small className="muted">{dateTimeText(row.created_at, i18n.language)}</small>
                  </td>
                  <td>{formatXaf(row.amount_xaf)}</td>
                  <td>{row.reason}</td>
                  <td>{row.requested_by_name}</td>
                  <td>
                    <Badge tone={row.status === "approved" ? "success" : row.status === "rejected" ? "danger" : "warning"}>{t(`support.refunds.statuses.${row.status}`)}</Badge>
                    {row.decided_by_name ? <small className="muted"> · {row.decided_by_name}</small> : null}
                    {row.status === "pending" && can("support.refunds.decide") ? (
                      <div className="stack">
                        <TextField label={t("support.refunds.note")} value={notes[row.id] ?? ""} error={errors.note} onChange={(e) => setNotes({ ...notes, [row.id]: e.target.value })} />
                        <div className="row">
                          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, data: { approve: true, note: notes[row.id] ?? "" } })}>
                            {t("support.refunds.approve")}
                          </Button>
                          <Button size="sm" variant="destructive" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, data: { approve: false, note: notes[row.id] ?? "" } })}>
                            {t("support.refunds.reject")}
                          </Button>
                        </div>
                        {errors.detail ? <p role="alert">{errors.detail}</p> : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </GlassCard>
  );
}
